import React from 'react';
import classNames from 'classnames';
import ReactMarkdown from 'react-markdown';

import { observer } from 'mobx-react';
import { Route, Router, Switch, useHistory, useLocation, useParams } from 'react-router-dom';

import { BooleanStore, ChoiceStore } from 'ui/base/input';
import { HistoryModalStore, Modal } from 'ui/base/modal';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { ButtonClickEvent, FormSubmitEvent } from 'ui/base/types';

import { PlainButton, ActiveButton, DefaultButton } from 'ui/public/button';

import { BackButton } from 'ui/base/button/back_button';
import { StandardPage } from 'ui/base/page/standard_page';

import { RadioInput } from 'ui/standard/input/radio_input';
import { SwitchInput } from 'ui/standard/input/switch_input';

import { ResultKind } from 'services/base/types';
import { IAccountsDomain, Pangolin } from 'services/accounts/types';
import { useAccountsDomain } from 'services/accounts/accounts_domain';

import { Redirect } from 'pages/core/redirect/redirect';

import {
  DeploymentProfile,
  getDeploymentProfileIconImage,
  getDeploymentProfileName,
  getDeploymentProfileSummary,
  deploymentProfileSettings,
  themedDeploymentProfileIconImageStyles,
  getDeploymentProfileDetails,
  themedDeploymentProfileGraphImageStyles,
  getDeploymentProfileGraphImage,
  getDeploymentProfileGuide,
  getDeploymentProfileGraphCoverage,
  getDeploymentProfileWarning,
} from 'assets/enums/deployment_profiles/deployment_profiles';

import { InterceptTuningStore } from './intercept_tuning_store';

import {
  themedStyles as modalThemedStyles,
  EnablePhysicalInterceptInfoModal,
  EnableUnicastSpoofingInfoModal,
  UseUnicastRequestInfoModal,
  UseUnicastResponseInfoModal,
  RedirectInternetWithUnicastInfoModal,
  RedirectInternalWithUnicastInfoModal,
  EnableBroadcastSpoofingInfoModal,
  UseBroadcastRequestInfoModal,
  UseBroadcastResponseInfoModal,
  SpoofGatewayWithBroadcastInfoModal,
  SpoofHostsWithBroadcastInfoModal,
  MasqueradeEgressInfoModal,
} from './intercept_tuning_modal';

import baseStyles from './intercept_tuning.module.css';
import Messages from './intercept_tuning_messages';

type InterceptTuningStyles = {
  header: string;
  headerBackButton: string;
  headerTitleText: string;
  headerSaveButton: string;

  content: string;

  section: string;

  field: string;
  fieldIcon: string;
  fieldOpen: string;
  fieldText: string;
  fieldLabel: string;
  fieldInfo: string;

  detail: string;

  clickable: string;

  cardContainer: string;
  cardDefault: string;
  cardDisabled: string;
  cardSelected: string;
  cardIcon: string;
  cardText: string;
  cardName: string;
  cardInfo: string;
  cardTags: string;

  graphContainer: string;
  graphNotes: string;
  graphWarning: string;
  graphFlowsTable: string;
  graphFlowLine: string;
  graphFlowDisabled: string;
  graphSettingsTable: string;

  help: string;
};

const themedStyles: ThemedStyles<InterceptTuningStyles> = {
  [Theme.DAYLIGHT]: {
    header: baseStyles.header,
    headerBackButton: baseStyles.headerBackButton,
    headerTitleText: baseStyles.daylightHeaderTitleText,
    headerSaveButton: baseStyles.daylightHeaderSaveButton,

    content: baseStyles.content,

    section: baseStyles.daylightSection,

    field: baseStyles.field,
    fieldIcon: baseStyles.fieldIcon,
    fieldOpen: baseStyles.daylightFieldOpen,
    fieldText: baseStyles.daylightFieldText,
    fieldLabel: baseStyles.daylightFieldLabel,
    fieldInfo: baseStyles.fieldInfo,

    detail: baseStyles.daylightDetail,

    clickable: baseStyles.clickable,

    cardContainer: baseStyles.cardContainer,
    cardDefault: baseStyles.daylightCardDefault,
    cardDisabled: baseStyles.daylightCardDisabled,
    cardSelected: baseStyles.daylightCardSelected,
    cardIcon: baseStyles.cardIcon,
    cardText: baseStyles.cardText,
    cardName: baseStyles.cardName,
    cardInfo: baseStyles.cardInfo,
    cardTags: baseStyles.daylightCardTags,

    graphContainer: baseStyles.graphContainer,
    graphNotes: baseStyles.graphNotes,
    graphWarning: baseStyles.daylightGraphWarning,
    graphFlowsTable: baseStyles.daylightGraphFlowsTable,
    graphFlowLine: baseStyles.graphFlowLine,
    graphFlowDisabled: baseStyles.graphFlowDisabled,
    graphSettingsTable: baseStyles.daylightGraphSettingsTable,

    help: baseStyles.daylightHelp,
  },
  [Theme.MIDNIGHT]: {
    header: baseStyles.header,
    headerBackButton: baseStyles.headerBackButton,
    headerTitleText: baseStyles.midnightHeaderTitleText,
    headerSaveButton: baseStyles.midnightHeaderSaveButton,

    content: baseStyles.content,

    section: baseStyles.midnightSection,

    field: baseStyles.field,
    fieldIcon: baseStyles.fieldIcon,
    fieldOpen: baseStyles.midnightFieldOpen,
    fieldText: baseStyles.midnightFieldText,
    fieldLabel: baseStyles.midnightFieldLabel,
    fieldInfo: baseStyles.fieldInfo,

    detail: baseStyles.midnightDetail,

    clickable: baseStyles.clickable,

    cardContainer: baseStyles.cardContainer,
    cardDefault: baseStyles.midnightCardDefault,
    cardDisabled: baseStyles.midnightCardDisabled,
    cardSelected: baseStyles.midnightCardSelected,
    cardIcon: baseStyles.cardIcon,
    cardText: baseStyles.cardText,
    cardName: baseStyles.cardName,
    cardInfo: baseStyles.cardInfo,
    cardTags: baseStyles.midnightCardTags,

    graphContainer: baseStyles.graphContainer,
    graphNotes: baseStyles.graphNotes,
    graphWarning: baseStyles.midnightGraphWarning,
    graphFlowsTable: baseStyles.midnightGraphFlowsTable,
    graphFlowLine: baseStyles.graphFlowLine,
    graphFlowDisabled: baseStyles.graphFlowDisabled,
    graphSettingsTable: baseStyles.midnightGraphSettingsTable,

    help: baseStyles.midnightHelp,
  },
};

type ProfileSelectInputProps = {
  styles: InterceptTuningStyles;
  choices: ChoiceStore<DeploymentProfile>;
  choice: DeploymentProfile;
};
const ProfileSelectInput = observer(({ styles, choices, choice }: ProfileSelectInputProps) => {
  const selected = choice === choices.selected;

  const profileIconImages = useStyles(themedDeploymentProfileIconImageStyles);
  const profileIcon = getDeploymentProfileIconImage(profileIconImages, choice);

  const profileName = getDeploymentProfileName(choice);
  const profileSummary = getDeploymentProfileSummary(choice);

  const onSelect = React.useCallback(() => {
    choices.setSelected(choice);
  }, [choices, choice]);

  return (
    <div onClick={onSelect}
         className={classNames(styles.clickable, styles.field,
                               selected ? styles.cardSelected : styles.cardDefault)}>
      <img className={styles.cardIcon} src={profileIcon} alt='Package Icon' />
      <div className={styles.cardText}>
        <div className={styles.cardName}>
          {profileName}
        </div>
        <div className={styles.cardInfo}>
          {profileSummary}
        </div>
      </div>
      <RadioInput
        store={choices}
        choice={choice}
      />
    </div>
  );
});

type ProfileSelectModalProps = {
  store: InterceptTuningStore;
  modal: HistoryModalStore;
  root: string;
};
const ProfileSelectModal = observer(({ store, modal, root }: ProfileSelectModalProps) => {
  const styles = useStyles(themedStyles);
  const modalStyles = useStyles(modalThemedStyles);

  const disabled = store.profileChoices.selected === undefined;

  const onNext = React.useCallback(() => {
    modal.history.push(`${root}/profiles/${store.profileChoices.selected}`);
  }, [modal, root, store.profileChoices.selected]);

  React.useEffect(() => {
    switch (store.settingsProfile) {
      case DeploymentProfile.PASSIVE:
        store.profileChoices.setSelected(DeploymentProfile.PASSIVE);
        break;
      case DeploymentProfile.ACTIVE_FULL:
        store.profileChoices.setSelected(DeploymentProfile.ACTIVE_FULL);
        break;
      case DeploymentProfile.ACTIVE_INTERNET:
        store.profileChoices.setSelected(DeploymentProfile.ACTIVE_INTERNET);
        break;
      default:
        store.profileChoices.setSelected(undefined);
        break;
    }
  }, [modal.visible, store.settingsProfile, store.profileChoices]);

  return(
    <div className={modalStyles.modalBig}>
      <StandardPage>
        <div className={modalStyles.modalHeader}>
          <div className={modalStyles.modalHeaderTitleText}>
            {Messages.profileSelectTitle()}
          </div>
          <button
            className={modalStyles.modalHeaderClose}
            onClick={modal.dismissModal}
          />
        </div>
        <div className={modalStyles.modalContent}>
          <div className={styles.cardContainer}>
            <ProfileSelectInput
              styles={styles}
              choices={store.profileChoices}
              choice={DeploymentProfile.PASSIVE}
            />
            <ProfileSelectInput
              styles={styles}
              choices={store.profileChoices}
              choice={DeploymentProfile.ACTIVE_FULL}
            />
            <ProfileSelectInput
              styles={styles}
              choices={store.profileChoices}
              choice={DeploymentProfile.ACTIVE_INTERNET}
            />
          </div>
          <ActiveButton type='button' onClick={onNext} disabled={disabled}>
            {Messages.buttonNext()}
          </ActiveButton>
          <DefaultButton type='button' onClick={modal.history.goBack}>
            {Messages.buttonBack()}
          </DefaultButton>
        </div>
      </StandardPage>
    </div>
  );
});

type ProfileConfirmModalProps = {
  store: InterceptTuningStore;
  modal: HistoryModalStore;
  root: string;
};
const ProfileConfirmModal = observer(({ store, modal }: ProfileConfirmModalProps) => {
  const { profile } = useParams<{ profile: DeploymentProfile }>();

  const styles = useStyles(themedStyles);
  const modalStyles = useStyles(modalThemedStyles);

  const profileIconImages = useStyles(themedDeploymentProfileIconImageStyles);
  const profileIcon = getDeploymentProfileIconImage(profileIconImages, profile);
  const profileName = getDeploymentProfileName(profile);
  const profileDetails = getDeploymentProfileDetails(profile);
  const profileGraphImages = useStyles(themedDeploymentProfileGraphImageStyles);
  const profileGraphImage = getDeploymentProfileGraphImage(profileGraphImages, profile);
  const profileGraphGuide = getDeploymentProfileGuide(profile);
  const profileGraphCoverage = getDeploymentProfileGraphCoverage(profile);
  const profileGraphWarning = getDeploymentProfileWarning(profile);
  const profileSettings = deploymentProfileSettings[profile];

  const onApply = React.useCallback((event: ButtonClickEvent) => {
    event.preventDefault();

    const profileValues = deploymentProfileSettings[profile];
    for (const [profileKey, profileValue] of Object.entries(profileValues)) {
      if (profileValue === undefined) continue;
      const storeKey = profileKey as keyof typeof store;
      if (Object.hasOwn(store, storeKey)) {
        if (typeof profileValue === 'boolean' &&
            typeof store[storeKey] === 'object' &&
            store[storeKey] instanceof BooleanStore) {
          const booleanStore = store[storeKey] as BooleanStore;
          booleanStore.setValue(profileValue);
        }
      }
    }

    modal.hideModal();
  }, [modal]);

  const enablePhysicalInterceptChanged = (
    profileSettings.enablePhysicalIntercept !== undefined &&
    profileSettings.enablePhysicalIntercept !== store.enablePhysicalIntercept.value
  );
  const enableUnicastSpoofingChanged = (
    profileSettings.enableUnicastSpoofing !== undefined &&
    profileSettings.enableUnicastSpoofing !== store.enableUnicastSpoofing.value
  );
  const useUnicastRequestChanged = (
    profileSettings.useUnicastRequest !== undefined &&
    profileSettings.useUnicastRequest !== store.useUnicastRequest.value
  );
  const useUnicastResponseChanged = (
    profileSettings.useUnicastResponse !== undefined &&
    profileSettings.useUnicastResponse !== store.useUnicastResponse.value
  );
  const redirectInternetWithUnicastChanged = (
    profileSettings.redirectInternetWithUnicast !== undefined &&
    profileSettings.redirectInternetWithUnicast !== store.redirectInternetWithUnicast.value
  );
  const redirectInternalWithUnicastChanged = (
    profileSettings.redirectInternalWithUnicast !== undefined &&
    profileSettings.redirectInternalWithUnicast !== store.redirectInternalWithUnicast.value
  );
  const enableBroadcastSpoofingChanged = (
    profileSettings.enableBroadcastSpoofing !== undefined &&
    profileSettings.enableBroadcastSpoofing !== store.enableBroadcastSpoofing.value
  );
  const useBroadcastRequestChanged = (
    profileSettings.useBroadcastRequest !== undefined &&
    profileSettings.useBroadcastRequest !== store.useBroadcastRequest.value
  );
  const useBroadcastResponseChanged = (
    profileSettings.useBroadcastResponse !== undefined &&
    profileSettings.useBroadcastResponse !== store.useBroadcastResponse.value
  );
  const spoofGatewayWithBroadcastChanged = (
    profileSettings.spoofGatewayWithBroadcast !== undefined &&
    profileSettings.spoofGatewayWithBroadcast !== store.spoofGatewayWithBroadcast.value
  );
  const spoofHostsWithBroadcastChanged = (
    profileSettings.spoofHostsWithBroadcast !== undefined &&
    profileSettings.spoofHostsWithBroadcast !== store.spoofHostsWithBroadcast.value
  );
  const masqueradeEgressChanged = (
    profileSettings.masqueradeEgress !== undefined &&
    profileSettings.masqueradeEgress !== store.masqueradeEgress.value
  );

  const settingsChanged = (
    enablePhysicalInterceptChanged ||
    enableUnicastSpoofingChanged ||
    useUnicastRequestChanged ||
    useUnicastResponseChanged ||
    redirectInternetWithUnicastChanged ||
    redirectInternalWithUnicastChanged ||
    enableBroadcastSpoofingChanged ||
    useBroadcastRequestChanged ||
    useBroadcastResponseChanged ||
    spoofGatewayWithBroadcastChanged ||
    spoofHostsWithBroadcastChanged ||
    masqueradeEgressChanged
  );

  return(
    <div className={modalStyles.modalLargest}>
      <StandardPage>
        <div className={modalStyles.modalHeader}>
          <div className={modalStyles.modalHeaderTitleText}>
            {Messages.profileConfirmTitle()}
          </div>
          <button
            className={modalStyles.modalHeaderClose}
            onClick={modal.dismissModal}
          />
        </div>
        <div className={modalStyles.modalContent}>
          <div className={styles.section}>
            <div className={styles.field}>
              <div className={styles.fieldText}>
                {profileName}
                <div className={styles.fieldInfo}>
                  {Messages.profileFieldInfo()}
                </div>
              </div>
              <img className={styles.fieldIcon} alt='Deployment Profile'
                   src={profileIcon} />
            </div>
            <div className={styles.detail}>
              <ReactMarkdown className={styles.detail}>
                {profileDetails}
              </ReactMarkdown>
            </div>
          </div>
          <div className={styles.graphContainer}>
            <img src={profileGraphImage} alt='Profile Graph' />
            <div className={styles.graphNotes}>
              {profileGraphGuide}
            </div>
            <table className={styles.graphFlowsTable}>
              <thead>
                <tr>
                  <th>{Messages.profileGraphFlowsTableHeaderFlow()}</th>
                  <th>{Messages.profileGraphFlowsTableHeaderLocation()}</th>
                  <th>↔</th>
                  <th>{Messages.profileGraphFlowsTableHeaderLocation()}</th>
                  <th>{Messages.profileGraphFlowsTableHeaderCovered()}</th>
                </tr>
              </thead>
              <tbody>
                <tr className={classNames({ [styles.graphFlowDisabled]: !profileGraphCoverage[0] })}>
                  <td><span style={{ backgroundColor: '#EF5350' }} className={styles.graphFlowLine} /></td>
                  <td>{Messages.profileGraphFlowsTableDataLocationWifi()}</td>
                  <td>↔</td>
                  <td>{Messages.profileGraphFlowsTableDataLocationInternet()}</td>
                  <td>{profileGraphCoverage[0] ? '✓' : '✕︀'}</td>
                </tr>
                <tr className={classNames({ [styles.graphFlowDisabled]: !profileGraphCoverage[1] })}>
                  <td><span style={{ backgroundColor: '#E97937' }} className={styles.graphFlowLine} /></td>
                  <td>{Messages.profileGraphFlowsTableDataLocationLan()}</td>
                  <td>↔</td>
                  <td>{Messages.profileGraphFlowsTableDataLocationInternet()}</td>
                  <td>{profileGraphCoverage[1] ? '✓' : '✕︀'}</td>
                </tr>
                <tr className={classNames({ [styles.graphFlowDisabled]: !profileGraphCoverage[2] })}>
                  <td><span style={{ backgroundColor: '#0087F5' }} className={styles.graphFlowLine} /></td>
                  <td>{Messages.profileGraphFlowsTableDataLocationWifi()}</td>
                  <td>↔</td>
                  <td>{Messages.profileGraphFlowsTableDataLocationLan()}</td>
                  <td>{profileGraphCoverage[2] ? '✓' : '✕︀'}</td>
                </tr>
                <tr className={classNames({ [styles.graphFlowDisabled]: !profileGraphCoverage[3] })}>
                  <td><span style={{ backgroundColor: '#FFC107' }} className={styles.graphFlowLine} /></td>
                  <td>{Messages.profileGraphFlowsTableDataLocationWifi()}</td>
                  <td>↔</td>
                  <td>{Messages.profileGraphFlowsTableDataLocationWifi()}</td>
                  <td>{profileGraphCoverage[3] ? '✓' : '✕︀'}</td>
                </tr>
                <tr className={classNames({ [styles.graphFlowDisabled]: !profileGraphCoverage[4] })}>
                  <td><span style={{ backgroundColor: '#73D277' }} className={styles.graphFlowLine} /></td>
                  <td>{Messages.profileGraphFlowsTableDataLocationLan()}</td>
                  <td>↔</td>
                  <td>{Messages.profileGraphFlowsTableDataLocationLan()}</td>
                  <td>{profileGraphCoverage[4] ? '✓' : '✕︀'}</td>
                </tr>
              </tbody>
            </table>
            <div className={styles.graphWarning}>
              {profileGraphWarning}
            </div>
            {settingsChanged && (<>
              <div className={styles.graphNotes}>
                {Messages.profileConfirmSettings()}
              </div>
              <table className={styles.graphSettingsTable}>
                <tbody>
                  {enablePhysicalInterceptChanged && (
                    <tr>
                      <td>
                        {Messages.enablePhysicalIntercept()}
                      </td>
                      <td>
                        {store.enablePhysicalIntercept.value ?
                         Messages.profileGraphSettingsTableDataOn() :
                         Messages.profileGraphSettingsTableDataOff()}
                      </td>
                      <td>→</td>
                      <td>
                        {profileSettings.enablePhysicalIntercept ?
                         Messages.profileGraphSettingsTableDataOn() :
                         Messages.profileGraphSettingsTableDataOff()}
                      </td>
                    </tr>
                  )}
                  {enableUnicastSpoofingChanged && (
                    <tr>
                      <td>
                        {Messages.enableUnicastSpoofing()}
                      </td>
                      <td>
                        {store.enableUnicastSpoofing.value ?
                         Messages.profileGraphSettingsTableDataOn() :
                         Messages.profileGraphSettingsTableDataOff()}
                      </td>
                      <td>→</td>
                      <td>
                        {profileSettings.enableUnicastSpoofing ?
                         Messages.profileGraphSettingsTableDataOn() :
                         Messages.profileGraphSettingsTableDataOff()}
                      </td>
                    </tr>
                  )}
                  {useUnicastRequestChanged && (
                    <tr>
                      <td>
                        {Messages.useUnicastRequest()}
                      </td>
                      <td>
                        {store.useUnicastRequest.value ?
                         Messages.profileGraphSettingsTableDataOn() :
                         Messages.profileGraphSettingsTableDataOff()}
                      </td>
                      <td>→</td>
                      <td>
                        {profileSettings.useUnicastRequest ?
                         Messages.profileGraphSettingsTableDataOn() :
                         Messages.profileGraphSettingsTableDataOff()}
                      </td>
                    </tr>
                  )}
                  {useUnicastResponseChanged && (
                    <tr>
                      <td>
                        {Messages.useUnicastResponse()}
                      </td>
                      <td>
                        {store.useUnicastResponse.value ?
                         Messages.profileGraphSettingsTableDataOn() :
                         Messages.profileGraphSettingsTableDataOff()}
                      </td>
                      <td>→</td>
                      <td>
                        {profileSettings.useUnicastResponse ?
                         Messages.profileGraphSettingsTableDataOn() :
                         Messages.profileGraphSettingsTableDataOff()}
                      </td>
                    </tr>
                  )}
                  {redirectInternetWithUnicastChanged && (
                    <tr>
                      <td>
                        {Messages.redirectInternetWithUnicast()}
                      </td>
                      <td>
                        {store.redirectInternetWithUnicast.value ?
                         Messages.profileGraphSettingsTableDataOn() :
                         Messages.profileGraphSettingsTableDataOff()}
                      </td>
                      <td>→</td>
                      <td>
                        {profileSettings.redirectInternetWithUnicast ?
                         Messages.profileGraphSettingsTableDataOn() :
                         Messages.profileGraphSettingsTableDataOff()}
                      </td>
                    </tr>
                  )}
                  {redirectInternalWithUnicastChanged && (
                    <tr>
                      <td>
                        {Messages.redirectInternalWithUnicast()}
                      </td>
                      <td>
                        {store.redirectInternalWithUnicast.value ?
                         Messages.profileGraphSettingsTableDataOn() :
                         Messages.profileGraphSettingsTableDataOff()}
                      </td>
                      <td>→</td>
                      <td>
                        {profileSettings.redirectInternalWithUnicast ?
                         Messages.profileGraphSettingsTableDataOn() :
                         Messages.profileGraphSettingsTableDataOff()}
                      </td>
                    </tr>
                  )}
                  {enableBroadcastSpoofingChanged && (
                    <tr>
                      <td>
                        {Messages.enableBroadcastSpoofing()}
                      </td>
                      <td>
                        {store.enableBroadcastSpoofing.value ?
                         Messages.profileGraphSettingsTableDataOn() :
                         Messages.profileGraphSettingsTableDataOff()}
                      </td>
                      <td>→</td>
                      <td>
                        {profileSettings.enableBroadcastSpoofing ?
                         Messages.profileGraphSettingsTableDataOn() :
                         Messages.profileGraphSettingsTableDataOff()}
                      </td>
                    </tr>
                  )}
                  {useBroadcastRequestChanged && (
                    <tr>
                      <td>
                        {Messages.useBroadcastRequest()}
                      </td>
                      <td>
                        {store.useBroadcastRequest.value ?
                         Messages.profileGraphSettingsTableDataOn() :
                         Messages.profileGraphSettingsTableDataOff()}
                      </td>
                      <td>→</td>
                      <td>
                        {profileSettings.useBroadcastRequest ?
                         Messages.profileGraphSettingsTableDataOn() :
                         Messages.profileGraphSettingsTableDataOff()}
                      </td>
                    </tr>
                  )}
                  {useBroadcastResponseChanged && (
                    <tr>
                      <td>
                        {Messages.useBroadcastResponse()}
                      </td>
                      <td>
                        {store.useBroadcastResponse.value ?
                         Messages.profileGraphSettingsTableDataOn() :
                         Messages.profileGraphSettingsTableDataOff()}
                      </td>
                      <td>→</td>
                      <td>
                        {profileSettings.useBroadcastResponse ?
                         Messages.profileGraphSettingsTableDataOn() :
                         Messages.profileGraphSettingsTableDataOff()}
                      </td>
                    </tr>
                  )}
                  {spoofGatewayWithBroadcastChanged && (
                    <tr>
                      <td>
                        {Messages.spoofGatewayWithBroadcast()}
                      </td>
                      <td>
                        {store.spoofGatewayWithBroadcast.value ?
                         Messages.profileGraphSettingsTableDataOn() :
                         Messages.profileGraphSettingsTableDataOff()}
                      </td>
                      <td>→</td>
                      <td>
                        {profileSettings.spoofGatewayWithBroadcast ?
                         Messages.profileGraphSettingsTableDataOn() :
                         Messages.profileGraphSettingsTableDataOff()}
                      </td>
                    </tr>
                  )}
                  {spoofHostsWithBroadcastChanged && (
                    <tr>
                      <td>
                        {Messages.spoofHostsWithBroadcast()}
                      </td>
                      <td>
                        {store.spoofHostsWithBroadcast.value ?
                         Messages.profileGraphSettingsTableDataOn() :
                         Messages.profileGraphSettingsTableDataOff()}
                      </td>
                      <td>→</td>
                      <td>
                        {profileSettings.spoofHostsWithBroadcast ?
                         Messages.profileGraphSettingsTableDataOn() :
                         Messages.profileGraphSettingsTableDataOff()}
                      </td>
                    </tr>
                  )}
                  {masqueradeEgressChanged && (
                    <tr>
                      <td>
                        {Messages.masqueradeEgress()}
                      </td>
                      <td>
                        {store.masqueradeEgress.value ?
                         Messages.profileGraphSettingsTableDataOn() :
                         Messages.profileGraphSettingsTableDataOff()}
                      </td>
                      <td>→</td>
                      <td>
                        {profileSettings.masqueradeEgress ?
                         Messages.profileGraphSettingsTableDataOn() :
                         Messages.profileGraphSettingsTableDataOff()}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </>)}
          </div>
          <ActiveButton type='button' onClick={onApply}>
            {Messages.buttonApply()}
          </ActiveButton>
          <DefaultButton type='button' onClick={modal.history.goBack}>
            {Messages.buttonBack()}
          </DefaultButton>
        </div>
      </StandardPage>
    </div>
  );
});

type BaseInterceptTuningProps = {
  accounts: IAccountsDomain;
  pangolin: Pangolin;
};
const BaseInterceptTuning = observer(({ accounts, pangolin }: BaseInterceptTuningProps) => {
  const history = useHistory();
  const location = useLocation();
  const styles = useStyles(themedStyles);

  const root = `${location.pathname}`;

  const [store] = React.useState(() => new InterceptTuningStore(pangolin));

  const profileIconImages = useStyles(themedDeploymentProfileIconImageStyles);
  const profileIcon = getDeploymentProfileIconImage(profileIconImages, store.settingsProfile);
  const profileName = getDeploymentProfileName(store.settingsProfile);
  const profileDetails = getDeploymentProfileDetails(store.settingsProfile);

  const onShowProfileSelectModal = React.useCallback(() => {
    store.profileModal.showModalAt(`${root}/profiles`);
  }, [store, root]);

  const onShowEnablePhysicalInterceptModal = React.useCallback(() => {
    store.enablePhysicalInterceptModal.showModal();
  }, [store]);

  const onShowEnableUnicastSpoofingModal = React.useCallback(() => {
    store.enableUnicastSpoofingModal.showModal();
  }, [store]);
  const onShowUseUnicastRequestModal = React.useCallback(() => {
    store.useUnicastRequestModal.showModal();
  }, [store]);
  const onShowUseUnicastResponseModal = React.useCallback(() => {
    store.useUnicastResponseModal.showModal();
  }, [store]);
  const onShowRedirectInternetWithUnicastModal = React.useCallback(() => {
    store.redirectInternetWithUnicastModal.showModal();
  }, [store]);
  const onShowRedirectInternalWithUnicastModal = React.useCallback(() => {
    store.redirectInternalWithUnicastModal.showModal();
  }, [store]);

  const onShowEnableBroadcastSpoofingModal = React.useCallback(() => {
    store.enableBroadcastSpoofingModal.showModal();
  }, [store]);
  const onShowUseBroadcastRequestModal = React.useCallback(() => {
    store.useBroadcastRequestModal.showModal();
  }, [store]);
  const onShowUseBroadcastResponseModal = React.useCallback(() => {
    store.useBroadcastResponseModal.showModal();
  }, [store]);
  const onShowSpoofGatewayWithBroadcastModal = React.useCallback(() => {
    store.spoofGatewayWithBroadcastModal.showModal();
  }, [store]);
  const onShowSpoofHostsWithBroadcastModal = React.useCallback(() => {
    store.spoofHostsWithBroadcastModal.showModal();
  }, [store]);

  const onShowMasqueradeEgressModal = React.useCallback(() => {
    store.masqueradeEgressModal.showModal();
  }, [store]);

  const onSubmit = React.useCallback(async(event: FormSubmitEvent) => {
    event.preventDefault();
    const result = await store.busy.perform(() => store.handleSave(accounts));
    if (result.kind === ResultKind.OK) {
      history.goBack();
    }
  }, [store, accounts, history]);

  return (
    <StandardPage>
      <Modal store={store.profileModal} onBackdropClick={store.profileModal.history.goBack}>
        <Router history={store.profileModal.history}>
          <Switch>
            <Route exact path={`${root}/profiles`}>
              <ProfileSelectModal modal={store.profileModal} store={store} root={root} />
            </Route>
            <Route exact path={`${root}/profiles/:profile`}>
              <ProfileConfirmModal modal={store.profileModal} store={store} root={root} />
            </Route>
          </Switch>
        </Router>
      </Modal>
      <Modal store={store.enablePhysicalInterceptModal}>
        <EnablePhysicalInterceptInfoModal store={store.enablePhysicalInterceptModal} />
      </Modal>
      <Modal store={store.enableUnicastSpoofingModal}>
        <EnableUnicastSpoofingInfoModal store={store.enableUnicastSpoofingModal} />
      </Modal>
      <Modal store={store.useUnicastRequestModal}>
        <UseUnicastRequestInfoModal store={store.useUnicastRequestModal} />
      </Modal>
      <Modal store={store.useUnicastResponseModal}>
        <UseUnicastResponseInfoModal store={store.useUnicastResponseModal} />
      </Modal>
      <Modal store={store.redirectInternetWithUnicastModal}>
        <RedirectInternetWithUnicastInfoModal store={store.redirectInternetWithUnicastModal} />
      </Modal>
      <Modal store={store.redirectInternalWithUnicastModal}>
        <RedirectInternalWithUnicastInfoModal store={store.redirectInternalWithUnicastModal} />
      </Modal>
      <Modal store={store.enableBroadcastSpoofingModal}>
        <EnableBroadcastSpoofingInfoModal store={store.enableBroadcastSpoofingModal} />
      </Modal>
      <Modal store={store.useBroadcastRequestModal}>
        <UseBroadcastRequestInfoModal store={store.useBroadcastRequestModal} />
      </Modal>
      <Modal store={store.useBroadcastResponseModal}>
        <UseBroadcastResponseInfoModal store={store.useBroadcastResponseModal} />
      </Modal>
      <Modal store={store.spoofGatewayWithBroadcastModal}>
        <SpoofGatewayWithBroadcastInfoModal store={store.spoofGatewayWithBroadcastModal} />
      </Modal>
      <Modal store={store.spoofHostsWithBroadcastModal}>
        <SpoofHostsWithBroadcastInfoModal store={store.spoofHostsWithBroadcastModal} />
      </Modal>
      <Modal store={store.masqueradeEgressModal}>
        <MasqueradeEgressInfoModal store={store.masqueradeEgressModal} />
      </Modal>
      <form onSubmit={onSubmit}>
        <div className={styles.header}>
          <div className={styles.headerBackButton}>
            <BackButton/>
          </div>
          <div className={styles.headerTitleText}>
            {Messages.title()}
          </div>
          <PlainButton
            type='submit'
            busy={store.busy}
            reserveBusyIconArea={true}
            className={styles.headerSaveButton}>
            {Messages.save()}
          </PlainButton>
        </div>
        <div className={styles.content}>
          <div className={styles.section}>
            <button className={classNames(styles.field, styles.clickable)}
                    type='button' onClick={onShowProfileSelectModal}>
              <div className={styles.fieldText}>
                {profileName}
                <div className={styles.fieldInfo}>
                  {Messages.profileFieldInfo()}
                </div>
              </div>
              <img className={styles.fieldIcon} alt='Deployment Profile'
                   src={profileIcon} />
              <div className={styles.fieldOpen}/>
            </button>
            <div className={styles.detail}>
              {profileDetails && (
                <ReactMarkdown className={styles.detail}>
                  {profileDetails}
                </ReactMarkdown>
              )}
            </div>
          </div>
          <div className={styles.section}>
            <div className={styles.field}>
              <div className={styles.fieldText}>
                {Messages.enablePhysicalIntercept()}
                <div className={styles.fieldLabel}>
                  {Messages.enablePhysicalInterceptHint()}
                </div>
              </div>
              <button
                type='button'
                className={styles.help}
                onClick={onShowEnablePhysicalInterceptModal} />
              <SwitchInput
                disabled={store.busy.value}
                store={store.enablePhysicalIntercept} />
            </div>
          </div>
          <div className={styles.section}>
            <div className={styles.field}>
              <div className={styles.fieldText}>
                {Messages.enableUnicastSpoofing()}
                <div className={styles.fieldLabel}>
                  {Messages.enableUnicastSpoofingHint()}
                </div>
              </div>
              <button
                type='button'
                className={styles.help}
                onClick={onShowEnableUnicastSpoofingModal} />
              <SwitchInput
                disabled={store.busy.value}
                store={store.enableUnicastSpoofing} />
            </div>
            <div className={styles.field}>
              <div className={styles.fieldText}>
                {Messages.useUnicastRequest()}
                <div className={styles.fieldLabel}>
                  {Messages.useUnicastRequestHint()}
                </div>
              </div>
              <button
                type='button'
                className={styles.help}
                onClick={onShowUseUnicastRequestModal} />
              <SwitchInput
                disabled={store.busy.value || !store.enableUnicastSpoofing.value}
                store={store.useUnicastRequest} />
            </div>
            <div className={styles.field}>
              <div className={styles.fieldText}>
                {Messages.useUnicastResponse()}
                <div className={styles.fieldLabel}>
                  {Messages.useUnicastResponseHint()}
                </div>
              </div>
              <button
                type='button'
                className={styles.help}
                onClick={onShowUseUnicastResponseModal} />
              <SwitchInput
                disabled={store.busy.value || !store.enableUnicastSpoofing.value}
                store={store.useUnicastResponse} />
            </div>
            <div className={styles.field}>
              <div className={styles.fieldText}>
                {Messages.redirectInternetWithUnicast()}
                <div className={styles.fieldLabel}>
                  {Messages.redirectInternetWithUnicastHint()}
                </div>
              </div>
              <button
                type='button'
                className={styles.help}
                onClick={onShowRedirectInternetWithUnicastModal} />
              <SwitchInput
                disabled={store.busy.value || !store.enableUnicastSpoofing.value || (
                  !store.useUnicastRequest.value && !store.useUnicastResponse.value
                )}
                store={store.redirectInternetWithUnicast} />
            </div>
            <div className={styles.field}>
              <div className={styles.fieldText}>
                {Messages.redirectInternalWithUnicast()}
                <div className={styles.fieldLabel}>
                  {Messages.redirectInternalWithUnicastHint()}
                </div>
              </div>
              <button
                type='button'
                className={styles.help}
                onClick={onShowRedirectInternalWithUnicastModal} />
              <SwitchInput
                disabled={store.busy.value || !store.enableUnicastSpoofing.value || (
                  !store.useUnicastRequest.value && !store.useUnicastResponse.value
                )}
                store={store.redirectInternalWithUnicast} />
            </div>
          </div>
          <div className={styles.section}>
            <div className={styles.field}>
              <div className={styles.fieldText}>
                {Messages.enableBroadcastSpoofing()}
                <div className={styles.fieldLabel}>
                  {Messages.enableBroadcastSpoofingHint()}
                </div>
              </div>
              <button
                type='button'
                className={styles.help}
                onClick={onShowEnableBroadcastSpoofingModal} />
              <SwitchInput
                disabled={store.busy.value}
                store={store.enableBroadcastSpoofing} />
            </div>
            <div className={styles.field}>
              <div className={styles.fieldText}>
                {Messages.useBroadcastRequest()}
                <div className={styles.fieldLabel}>
                  {Messages.useBroadcastRequestHint()}
                </div>
              </div>
              <button
                type='button'
                className={styles.help}
                onClick={onShowUseBroadcastRequestModal} />
              <SwitchInput
                disabled={store.busy.value || !store.enableBroadcastSpoofing.value}
                store={store.useBroadcastRequest} />
            </div>
            <div className={styles.field}>
              <div className={styles.fieldText}>
                {Messages.useBroadcastResponse()}
                <div className={styles.fieldLabel}>
                  {Messages.useBroadcastResponseHint()}
                </div>
              </div>
              <button
                type='button'
                className={styles.help}
                onClick={onShowUseBroadcastResponseModal} />
              <SwitchInput
                disabled={store.busy.value || !store.enableBroadcastSpoofing.value}
                store={store.useBroadcastResponse} />
            </div>
            <div className={styles.field}>
              <div className={styles.fieldText}>
                {Messages.spoofGatewayWithBroadcast()}
                <div className={styles.fieldLabel}>
                  {Messages.spoofGatewayWithBroadcastHint()}
                </div>
              </div>
              <button
                type='button'
                className={styles.help}
                onClick={onShowSpoofGatewayWithBroadcastModal} />
              <SwitchInput
                disabled={store.busy.value || !store.enableBroadcastSpoofing.value || (
                  !store.useBroadcastRequest.value && !store.useBroadcastResponse.value
                )}
                store={store.spoofGatewayWithBroadcast} />
            </div>
            <div className={styles.field}>
              <div className={styles.fieldText}>
                {Messages.spoofHostsWithBroadcast()}
                <div className={styles.fieldLabel}>
                  {Messages.spoofHostsWithBroadcastHint()}
                </div>
              </div>
              <button
                type='button'
                className={styles.help}
                onClick={onShowSpoofHostsWithBroadcastModal} />
              <SwitchInput
                disabled={store.busy.value || !store.enableBroadcastSpoofing.value || (
                  !store.useBroadcastRequest.value && !store.useBroadcastResponse.value
                )}
                store={store.spoofHostsWithBroadcast} />
            </div>
          </div>
          <div className={styles.section}>
            <div className={styles.field}>
              <div className={styles.fieldText}>
                {Messages.masqueradeEgress()}
                <div className={styles.fieldLabel}>
                  {Messages.masqueradeEgressHint()}
                </div>
              </div>
              <button
                type='button'
                className={styles.help}
                onClick={onShowMasqueradeEgressModal} />
              <SwitchInput
                disabled={store.busy.value}
                store={store.masqueradeEgress} />
            </div>
          </div>
        </div>
      </form>
    </StandardPage>
  );
});

export const InterceptTuning = observer(() => {
  const { pangolinID } = useParams<{ pangolinID: string }>();
  if (!pangolinID) return <Redirect />;

  const accounts = useAccountsDomain();

  const pangolin = accounts.pangolins.get(pangolinID);
  if (!pangolin) return <Redirect />;

  return (
    <BaseInterceptTuning
      accounts={accounts}
      pangolin={pangolin}
    />
  );
});
